/**
 * Decision Approvals Page
 * 
 * Consultant view for pending decision approvals.
 * Shows all blocked decisions awaiting consultant review.
 * 
 * @version 1.0.0
 * @date 2026-01-29
 */

import { useState } from 'react';
import { Shield, CheckCircle, Loader2, AlertTriangle } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { DecisionApprovalCard } from './decision/DecisionApprovalCard';
import { usePendingDecisions } from '../hooks/usePendingDecisions';
import { useDecisionEvaluation } from '../hooks/useDecisionEvaluation';
import { projectId } from '../utils/supabase/info';
import { toast } from 'sonner';

interface DecisionApprovalsPageProps {
  accessToken: string;
  currentUser: any;
}

export function DecisionApprovalsPage({ accessToken, currentUser }: DecisionApprovalsPageProps) {
  const [selectedDecision, setSelectedDecision] = useState<any>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const { pendingDecisions, loading, error, refetch, pendingCount } = usePendingDecisions(
    currentUser.id,
    accessToken,
    true
  );

  const handleApprove = async (historyId: string, notes: string) => {
    try {
      // Extract companyId from historyId or from decision object
      const decision = pendingDecisions.find((d) => d.historyId === historyId);
      if (!decision) {
        throw new Error('Decision not found');
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/decision-history/${decision.companyId}/approve`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            historyId,
            approvedBy: currentUser.id,
            consultantNotes: notes,
            approved: true,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to approve decision');
      }

      await refetch();
      toast.success('Decision approved successfully');
    } catch (error) {
      console.error('Error approving decision:', error);
      throw error;
    }
  };

  const handleDeny = async (historyId: string, notes: string) => {
    try {
      const decision = pendingDecisions.find((d) => d.historyId === historyId);
      if (!decision) {
        throw new Error('Decision not found');
      }

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/decision-history/${decision.companyId}/approve`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            historyId,
            approvedBy: currentUser.id,
            consultantNotes: notes,
            approved: false,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to deny decision');
      }

      await refetch();
      toast.success('Decision denied - client will be notified');
    } catch (error) {
      console.error('Error denying decision:', error);
      throw error;
    }
  };

  const handleViewDetails = (decision: any) => {
    setSelectedDecision(decision);
    setShowDetailsModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="p-6">
          <div className="flex items-center gap-3">
            <AlertTriangle className="size-6 text-red-600" />
            <div>
              <p className="font-medium text-red-900">Error Loading Pending Approvals</p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
          <Button onClick={refetch} variant="outline" className="mt-4">
            Try Again
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="size-6 text-orange-600" />
            Decision Approvals
          </h2>
          <p className="text-gray-600 mt-1">
            Review and approve blocked architectural decisions
          </p>
        </div>
        <Button onClick={refetch} variant="outline" size="sm">
          Refresh
        </Button>
      </div>

      {/* Pending Count Badge */}
      {pendingCount > 0 && (
        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0 w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <span className="text-lg font-bold text-orange-700">{pendingCount}</span>
              </div>
              <div>
                <p className="font-medium text-orange-900">
                  {pendingCount} decision{pendingCount !== 1 ? 's' : ''} awaiting your review
                </p>
                <p className="text-sm text-orange-700">
                  Clients are blocked from proceeding until you approve or deny
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty State */}
      {pendingCount === 0 && (
        <Card>
          <CardContent className="p-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle className="size-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">All Caught Up</h3>
            <p className="text-gray-600">
              There are no pending decision approvals at this time.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Pending Decisions List */}
      {pendingCount > 0 && (
        <div className="space-y-4">
          {pendingDecisions.map((decision) => (
            <DecisionApprovalCard
              key={decision.historyId}
              decision={decision}
              onApprove={handleApprove}
              onDeny={handleDeny}
              onViewDetails={handleViewDetails}
              userId={currentUser.id}
              accessToken={accessToken}
            />
          ))}
        </div>
      )}

      {/* Details Modal (simplified - could be enhanced) */}
      {showDetailsModal && selectedDecision && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold">Decision Details</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowDetailsModal(false)}
                >
                  Close
                </Button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium text-gray-700">Company</p>
                  <p className="text-gray-900">{selectedDecision.companyName}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Decision</p>
                  <p className="text-gray-900">{selectedDecision.decisionTitle}</p>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-700">Selected Option</p>
                  <p className="text-gray-900">{selectedDecision.selectedOption}</p>
                </div>
                {selectedDecision.overrideReason && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">Client Justification</p>
                    <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-1">
                      <p className="text-sm text-blue-900">{selectedDecision.overrideReason}</p>
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-2">Full Decision Context</p>
                  <p className="text-sm text-gray-600">
                    View the complete decision node evaluation, consequences, and rationale in the
                    space planning wizard context.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}